/**
 * 計画系 3 タブの参照マスタ (BG / submitter) の submit 前 validation ヘルパー
 * (4526-plan-master-valid-period-filter 設計書 §状態マトリクス 対応の submit
 * block layer)。
 *
 * fallback option の目的別責務:
 * - **物理削除** (allBgMasters にも無い): AC-06 の「(削除済み)」表示。既登録
 *   参照を Edit modal で維持し update 可能にする。submit block **しない**。
 * - **有効期間外** (allBgMasters には存在するが filteredCandidates から外れた):
 *   AC-01/02/03 の narrow が「候補に出さない」と規定した対象。fallback option
 *   で UI 上は残るが、submit したら失効参照が RPC 送信される silent invalid
 *   submission。submit block **する**。
 * - **allMasters 未取得**: `undefined` を渡す。物理削除判定を行えないため
 *   fail-closed で block する (fetch 未完了で候補外 = 有効期間外扱い)。
 *
 * 本ヘルパーは「有効期間外」ケースのみを検出する (物理削除は除外)。
 */

export type MinimalEntry = {
  readonly bgMasterId: string
}

export type MinimalMasterRow = {
  readonly id: string
}

export type OutOfPeriodEntry = {
  readonly index: number
  readonly bgMasterId: string
}

/**
 * `entries[i].bgMasterId` のうち、`filteredCandidates` に含まれず、かつ
 * `allMasters` には存在する (= 有効期間外による候補外) ものを検出する。
 * 空文字は Zod schema.min(1) が別途扱うので対象外。
 * `allMasters` にも無い (= 物理削除) は AC-06 尊重で対象外。
 * `allMasters === undefined` (= fetch 未完了) は物理削除と区別できないため
 * fail-closed で候補外の全 entry を out-of-period 扱いにする。
 */
export function findBgsOutOfPeriod<
  E extends MinimalEntry,
  C extends MinimalMasterRow,
  A extends MinimalMasterRow,
>(
  entries: readonly E[],
  filteredCandidates: readonly C[],
  allMasters: readonly A[] | undefined,
): OutOfPeriodEntry[] {
  const result: OutOfPeriodEntry[] = []
  for (const [index, entry] of entries.entries()) {
    if (entry.bgMasterId === '') continue
    const inCandidates = filteredCandidates.some(
      (c) => c.id === entry.bgMasterId,
    )
    if (inCandidates) continue
    // allMasters 未取得時は物理削除と区別できないため fail-closed で block
    // する (fetch 未完了で候補外の値は「有効期間外の可能性がある」ため submit
    // を止めて user に再選択させる)。
    if (allMasters !== undefined) {
      const inAllMasters = allMasters.some((a) => a.id === entry.bgMasterId)
      if (!inAllMasters) continue
    }
    result.push({ index, bgMasterId: entry.bgMasterId })
  }
  return result
}
